import { createSlice } from '@reduxjs/toolkit';
import {
  fetchDoctors,
  fetchPendingDoctors,
  fetchDoctorById,
  fetchDoctorStats,
  validateDoctor,
  loginAdmin,
  initAuth,
  logoutAdmin,
  fetchFeedbacks,
} from '../thunks/adminThunks';

const initialState = {
  // ── Doctors ──────────────────────────────
  items: [],
  total: 0,
  current: null,
  stats: null,
  status: 'idle',   // idle | loading | succeeded | failed
  error: null,

  // ── Auth ─────────────────────────────────
  admin: null,
  isAuthenticated: false,
  authStatus: 'idle',   // idle | loading | succeeded | failed
  authError: null,

  feedbacks: [],
  feedbackTotal: 0,
  feedbackCurrent: null,
  feedbackAverage: null,
  feedbackStatus: 'idle',
  feedbackError: null,
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // Doctors
    clearDoctorCurrent(state) {
      state.current = null;
    },
    setDoctorStatusLocal(state, action) {
      const { id, status } = action.payload;
      state.items = state.items.map((d) =>
        String(d.id) === String(id) ? { ...d, status } : d
      );
      if (state.current?.id && String(state.current.id) === String(id)) {
        state.current.status = status;
      }
    },
    resetDoctorsState(state) {
      state.items = [];
      state.total = 0;
      state.current = null;
      state.stats = null;
      state.status = 'idle';
      state.error = null;
    },

    // Auth
    logoutLocal(state) {
      state.admin = null;
      state.isAuthenticated = false;
      state.authStatus = 'idle';
      state.authError = null;
    },
    resetAuthError(state) {
      state.authError = null;
    },
  },
  extraReducers: (builder) => {
    builder

    .addCase(fetchFeedbacks.pending, (state) => {
      state.feedbackStatus = 'loading';
      state.feedbackError = null;
    })
    .addCase(fetchFeedbacks.fulfilled, (state, action) => {
      state.feedbackStatus = 'succeeded';
      state.feedbacks = action.payload.items ?? [];
      state.feedbackTotal = action.payload.total ?? 0;
    })
    .addCase(fetchFeedbacks.rejected, (state, action) => {
      state.feedbackStatus = 'failed';
      state.feedbackError = action.payload;
    })

    // ── logoutAdmin ───────────────────────
      .addCase(logoutAdmin.fulfilled, (state) => {
        state.admin = null;
        state.isAuthenticated = false;
        state.authStatus = 'idle';
        state.authError = null;

        // on peut aussi reset la partie doctors si on veut
        state.items = [];
        state.total = 0;
        state.current = null;
        state.stats = null;
        state.status = 'idle';
        state.error = null;
      })
      .addCase(logoutAdmin.rejected, (state) => {
        // même en cas d'erreur backend, on peut forcer le logout côté front
        state.admin = null;
        state.isAuthenticated = false;
        state.authStatus = 'idle';
        state.authError = null;

        state.items = [];
        state.total = 0;
        state.current = null;
        state.stats = null;
        state.status = 'idle';
        state.error = null;
      })

    .addCase(initAuth.pending, (state) => {
      state.authStatus = 'loading';
    })
    .addCase(initAuth.fulfilled, (state, action) => {
      state.authStatus      = 'succeeded';
      state.admin           = action.payload ?? null; // payload = user directement
      state.isAuthenticated = !!action.payload;       // true si user existe
    })
    .addCase(initAuth.rejected, (state) => {
      state.authStatus      = 'idle';
      state.isAuthenticated = false;
      state.admin           = null;
    })
      // ── loginAdmin ────────────────────────
      .addCase(loginAdmin.pending, (state) => {
        state.authStatus = 'loading';
        state.authError = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.authStatus = 'succeeded';
        state.admin = action.payload?.admin ?? null;
        state.isAuthenticated = true;
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.authStatus = 'failed';
        state.authError = action.payload;
        state.isAuthenticated = false;
        state.admin = null;
      })

      // ── fetchDoctors ──────────────────────
      .addCase(fetchDoctors.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
  state.status = 'succeeded';
  state.items = action.payload.items ?? [];   // ✅ IMPORTANT
  state.total = action.payload.total ?? 0;
  state.error = null;

  // ✅ LOG CRITIQUE : vérifier ce qui est réellement dans le state

})
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ── fetchPendingDoctors ───────────────
      .addCase(fetchPendingDoctors.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPendingDoctors.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchPendingDoctors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ── fetchDoctorById ───────────────────
      .addCase(fetchDoctorById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
        state.current = null;
      })
      .addCase(fetchDoctorById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload;
      })
      .addCase(fetchDoctorById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ── fetchDoctorStats ──────────────────
      .addCase(fetchDoctorStats.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDoctorStats.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.stats = action.payload;
      })
      .addCase(fetchDoctorStats.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // ── validateDoctor ────────────────────
      .addCase(validateDoctor.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(validateDoctor.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.items = state.items.map((d) =>
          String(d.id) === String(updated.id) ? updated : d
        );
        if (state.current?.id && String(state.current.id) === String(updated.id)) {
          state.current = updated;
        }
      })
      .addCase(validateDoctor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const {
  clearDoctorCurrent,
  setDoctorStatusLocal,
  resetDoctorsState,
  logoutLocal,
  resetAuthError,
} = adminSlice.actions;

export default adminSlice.reducer;